import type { MetaDescriptor } from 'react-router';

/** SCAPI's evaluated, localized Page Meta Tag Rule result. */
export type PageMetaTag = {
    id?: string;
    type?: string;
    value?: string;
};

export type PageSeoInput = {
    pageMetaTags?: readonly PageMetaTag[];
    pageTitle?: string | null;
    pageDescription?: string | null;
    title?: string;
    description?: string;
    rawTitle?: boolean;
};

/** Resolve each field separately. SFCC owns the complete authored title, including branding. */
export function resolvePageSeo({
    pageMetaTags,
    pageTitle,
    pageDescription,
    title,
    description,
    rawTitle,
}: PageSeoInput) {
    const ruleTitle = pageMetaTags?.find(
        (tag) => (tag.type === 'title' || (!tag.type && tag.id === 'title')) && tag.value?.trim()
    )?.value;
    const ruleDescription = pageMetaTags?.find(
        (tag) => tag.id === 'description' && (!tag.type || tag.type === 'name') && tag.value?.trim()
    )?.value;
    const authoredTitle = ruleTitle || (pageTitle?.trim() ? pageTitle : undefined);

    return {
        title: authoredTitle || title,
        description: ruleDescription || (pageDescription?.trim() ? pageDescription : undefined) || description,
        rawTitle: Boolean(authoredTitle || rawTitle),
    };
}

/**
 * Builds the full document `<title>` string from a resolved base title and the site name.
 * When `rawTitle` is set the title is used verbatim; otherwise ` | {siteName}` is appended.
 * When no `title` is provided the site name alone is used. Shared by `SeoMeta` (which drives
 * the `<title>` tag) and analytics, so the `page_view` title always matches the rendered tag.
 */
export function buildSeoTitle({
    title,
    rawTitle,
    siteName,
}: {
    title?: string;
    rawTitle?: boolean;
    siteName: string;
}): string {
    if (!title) {
        return siteName;
    }
    return rawTitle ? title : `${title} | ${siteName}`;
}

/**
 * Error view title from the serialized `routeError` namespace, using the same status → key
 * mapping as the error boundaries (`404` / `403` / `500`, otherwise `defaultTitle`).
 */
export function resolveErrorPageTitle(errorTranslations: Record<string, unknown>, status?: number): string | undefined {
    const entry = status === 404 || status === 403 || status === 500 ? errorTranslations[status] : undefined;
    const title = entry && typeof entry === 'object' && 'title' in entry ? entry.title : errorTranslations.defaultTitle;
    return typeof title === 'string' ? title : undefined;
}

/**
 * Document-head descriptors for React Router's `<Meta />`.
 * Render titles here instead of `<SeoMeta>` so the `<title>` stays in `<head>`
 * through SSR and hydration.
 */
export function buildPageSeoDescriptors({
    title,
    rawTitle,
    description,
    siteName,
    robots,
    prevUrl,
    nextUrl,
    openGraph,
}: {
    title?: string;
    rawTitle?: boolean;
    description?: string;
    siteName: string;
    robots?: string;
    prevUrl?: string;
    nextUrl?: string;
    openGraph?: {
        type?: 'website' | 'product' | 'article';
        url?: string;
        image?: string;
    };
}): MetaDescriptor[] {
    const fullTitle = buildSeoTitle({ title, rawTitle, siteName });
    const socialTitle = title || siteName;
    const descriptors: MetaDescriptor[] = [{ title: fullTitle }];

    if (description) descriptors.push({ name: 'description', content: description });
    if (robots) descriptors.push({ name: 'robots', content: robots });
    if (prevUrl) descriptors.push({ tagName: 'link', rel: 'prev', href: prevUrl });
    if (nextUrl) descriptors.push({ tagName: 'link', rel: 'next', href: nextUrl });

    if (openGraph) {
        descriptors.push(
            { property: 'og:title', content: socialTitle },
            { property: 'og:type', content: openGraph.type ?? 'website' },
            { property: 'og:site_name', content: siteName }
        );
        if (description) descriptors.push({ property: 'og:description', content: description });
        if (openGraph.url) descriptors.push({ property: 'og:url', content: openGraph.url });
        if (openGraph.image) descriptors.push({ property: 'og:image', content: openGraph.image });

        const twitterCard = openGraph.image ? 'summary_large_image' : 'summary';
        descriptors.push(
            { name: 'twitter:card', content: twitterCard },
            { name: 'twitter:title', content: socialTitle }
        );
        if (description) descriptors.push({ name: 'twitter:description', content: description });
        if (openGraph.image) descriptors.push({ name: 'twitter:image', content: openGraph.image });
    }

    return descriptors;
}

type MetaMatch = {
    id: string;
    meta: MetaDescriptor[];
    loaderData?: unknown;
};

type RootSeoLoaderData = {
    seoSiteName?: string;
};

/** Root canonical and hreflang descriptors. A leaf `meta` export replaces its parents, so callers copy these forward. */
export function getRootMeta(matches: readonly (MetaMatch | undefined)[]) {
    const rootMatch = matches.find((match) => match?.id === 'root');
    const descriptors = rootMatch?.meta ?? [];

    return {
        descriptors,
        canonical: descriptors.filter(
            (descriptor) => 'tagName' in descriptor && descriptor.tagName === 'link' && descriptor.rel === 'canonical'
        ),
        alternates: descriptors.filter(
            (descriptor) => 'tagName' in descriptor && descriptor.tagName === 'link' && descriptor.rel === 'alternate'
        ),
        siteName: (rootMatch?.loaderData as RootSeoLoaderData | undefined)?.seoSiteName,
    };
}
